import React, { useState } from "react";
import { Box, Chip, Link, Stack, Tooltip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import {
  buildWhatsappInteractivePresentation,
  isTechnicalWhatsappChoiceId,
} from "../../utils/whatsappInteractiveDisplay";

function isWhatsappContext(message, defaultChannel) {
  const blob = `${message?.channel || ""} ${defaultChannel || ""}`.toLowerCase();
  return /\bwhatsapp\b/.test(blob);
}

function bodyAlreadyListsOptions(text) {
  return /\nOpções:\s*\n/.test(String(text || ""));
}

const chipSx = {
  height: 22,
  maxWidth: "100%",
  fontSize: "0.75rem",
  fontWeight: 500,
  bgcolor: "action.hover",
  border: "1px solid",
  borderColor: "divider",
  color: "text.secondary",
  "& .MuiChip-label": {
    px: 0.75,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

function OptionChip({ label, title }) {
  const chip = <Chip size="small" label={label} sx={chipSx} />;
  if (!title || title === label) return chip;
  return (
    <Tooltip title={title} placement="top">
      {chip}
    </Tooltip>
  );
}

/**
 * Lê o que foi enviado ao cliente (respostas, link ou lista) sem virar ação do atendente.
 */
export default function WhatsappInteractiveBlocks({ message, isClient }) {
  const { t } = useTranslation("chat");
  const [listOpen, setListOpen] = useState(false);
  const pres = buildWhatsappInteractivePresentation(message, null, null);
  const view = pres.clientFacing;

  if (!view || isClient || bodyAlreadyListsOptions(message?.text)) return null;

  const prefix = (
    <Tooltip title={t("clientOptions.hint", "Opções que o cliente vê na conversa.")} placement="top">
      <Typography
        component="span"
        sx={{
          fontSize: "0.6875rem",
          lineHeight: "22px",
          color: "text.disabled",
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        {t("clientOptions.prefix", "Para o cliente")}
      </Typography>
    </Tooltip>
  );

  if (view.mode === "cta" && view.url) {
    const tip = view.fullLabel && view.fullLabel !== view.label ? `${view.fullLabel}\n${view.url}` : view.url;
    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
        {prefix}
        <Tooltip title={tip} placement="top">
          <Chip
            size="small"
            component={Link}
            href={view.url}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            label={view.label}
            icon={<OpenInNewIcon sx={{ fontSize: "12px !important" }} />}
            onClick={(event) => event.stopPropagation()}
            sx={{ ...chipSx, textDecoration: "none" }}
          />
        </Tooltip>
      </Stack>
    );
  }

  if (view.mode === "list" && view.items?.length) {
    const countLabel = t("clientOptions.listChip", {
      label: view.listButtonText || "Ver opções",
      count: view.items.length,
      defaultValue: "{{label}} · {{count}}",
    });
    return (
      <Box sx={{ mt: 0.75 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
          {prefix}
          <Chip
            size="small"
            label={countLabel}
            onClick={(event) => {
              event.stopPropagation();
              setListOpen((open) => !open);
            }}
            aria-expanded={listOpen}
            aria-label={
              listOpen
                ? t("clientOptions.hideList", "Ocultar as opções da lista")
                : t("clientOptions.showList", "Ver as opções da lista")
            }
            sx={{ ...chipSx, cursor: "pointer" }}
          />
        </Stack>
        {listOpen ? (
          <Box
            sx={{
              mt: 0.5,
              ml: 0.25,
              pl: 1,
              maxHeight: 132,
              overflowY: "auto",
              borderLeft: "2px solid",
              borderColor: "divider",
            }}
          >
            {view.items.map((item, index) => (
              <Typography
                key={`${item.label}-${index}`}
                sx={{ fontSize: "0.75rem", lineHeight: 1.45, color: "text.secondary", py: 0.15 }}
              >
                {item.label}
                {item.description && item.description !== item.label ? (
                  <Box component="span" sx={{ color: "text.disabled" }}>
                    {" "}
                    · {item.description}
                  </Box>
                ) : null}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  }

  if (view.mode === "carousel" && view.items?.length) {
    return (
      <Box sx={{ mt: 0.75 }}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
          {prefix}
        </Stack>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 0.5,
          }}
        >
          {view.items.map((item, index) => (
            <Box
              key={`${item.id || item.label}-${index}`}
              sx={{
                flex: "0 0 168px",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              {item.imageUrl ? (
                <Box
                  component="img"
                  src={item.imageUrl}
                  alt=""
                  sx={{ width: "100%", height: 72, objectFit: "cover", display: "block", bgcolor: "action.hover" }}
                />
              ) : null}
              <Box sx={{ px: 1, py: 0.75 }}>
                <Typography sx={{ fontSize: "0.75rem", whiteSpace: "pre-line", color: "text.secondary" }}>
                  {item.description}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "success.main", mt: 0.5 }}>
                  {item.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (view.mode !== "reply" || !view.items?.length) return null;

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
      {prefix}
      {view.items.map((item, index) => (
        <OptionChip key={`${item.label}-${index}`} label={item.label} title={item.fullLabel} />
      ))}
    </Stack>
  );
}

/**
 * Rodapé para respostas do cliente: referência técnica quando o texto foi traduzido para rótulo legível.
 */
export function WhatsappInboundChoiceFooter({ message, textOverride, defaultChannel }) {
  const isWa = isWhatsappContext(message, defaultChannel);
  const raw = message?.text != null ? String(message.text).trim() : "";
  if (!isWa || !textOverride || !raw || textOverride === raw) return null;
  if (!isTechnicalWhatsappChoiceId(raw)) return null;

  return (
    <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.78 }}>
      ID no WhatsApp: <Box component="code" sx={{ fontSize: "0.8em" }}>{raw}</Box>
    </Typography>
  );
}
